import { useState, useRef, useCallback } from 'react';
import { Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface VariantAttributeValue {
  id: string;
  value: string;
}

export interface VariantAttribute {
  id: string;
  name: string;
  values: string[];
  /** Full value objects with IDs for deletion */
  valueObjects: VariantAttributeValue[];
}

export interface ProductVariantConfig {
  attributes: {
    attributeId: string;
    attributeName: string;
    values: string[];
  }[];
}

interface VariantAttributesEditorProps {
  config: ProductVariantConfig;
  onChange: (config: ProductVariantConfig) => void;
  disabled?: boolean;
  /** When true, shows guidance tailored for composite+variant products (e.g. size tiers) */
  compositeVariantMode?: boolean;
}

export function VariantAttributesEditor({
  config,
  onChange,
  disabled = false,
  compositeVariantMode = false,
}: VariantAttributesEditorProps) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newValueInputs, setNewValueInputs] = useState<Record<string, string>>({});
  const [creatingAttribute, setCreatingAttribute] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const valueInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const setValueInputRef = useCallback((attributeId: string) => (el: HTMLInputElement | null) => {
    valueInputRefs.current[attributeId] = el;
  }, []);

  // Fetch organization's variant attributes
  const { data: orgAttributes = [], isLoading: attributesLoading } = useQuery({
    queryKey: ['variant-attributes', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return [];

      const { data, error } = await supabase
        .from('variant_attributes')
        .select(`
          id,
          name,
          variant_attribute_values (
            id,
            value
          )
        `)
        .eq('organization_id', profile.organization_id)
        .order('sort_order');

      if (error) throw error;

      return (data || []).map((attr: any) => ({
        id: attr.id,
        name: attr.name,
        values: (attr.variant_attribute_values || []).map((v: any) => v.value),
        valueObjects: (attr.variant_attribute_values || []).map((v: any) => ({ id: v.id, value: v.value })),
      })) as VariantAttribute[];
    },
    enabled: !!profile?.organization_id,
  });

  // Create new attribute mutation
  const createAttribute = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from('variant_attributes')
        .insert({
          organization_id: profile!.organization_id,
          name: name.trim(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes'] });
      setNewAttributeName('');
      setCreatingAttribute(false);
    },
  });

  // Create new attribute value mutation
  const createAttributeValue = useMutation({
    mutationFn: async ({ attributeId, value }: { attributeId: string; value: string }) => {
      const { data, error } = await supabase
        .from('variant_attribute_values')
        .insert({
          attribute_id: attributeId,
          value: value.trim(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes'] });
    },
  });

  // Delete an attribute value mutation
  const deleteAttributeValue = useMutation({
    mutationFn: async (valueId: string) => {
      const { error } = await supabase
        .from('variant_attribute_values')
        .delete()
        .eq('id', valueId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes'] });
    },
  });

  // Delete an entire attribute mutation
  const deleteAttribute = useMutation({
    mutationFn: async (attributeId: string) => {
      const { error } = await supabase
        .from('variant_attributes')
        .delete()
        .eq('id', attributeId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variant-attributes'] });
    },
  });

  // Handle deleting a value from an attribute (permanent, removes from org)
  const handleDeleteValue = (attributeId: string, value: string) => {
    const orgAttr = orgAttributes.find(a => a.id === attributeId);
    const valObj = orgAttr?.valueObjects.find(v => v.value === value);
    if (!valObj) return;

    // Also unselect it from the product config if selected
    onChange({
      ...config,
      attributes: config.attributes.map(attr => {
        if (attr.attributeId !== attributeId) return attr;
        return { ...attr, values: attr.values.filter(v => v !== value) };
      }),
    });

    deleteAttributeValue.mutate(valObj.id);
  };

  // Handle deleting an entire attribute (permanent, removes from org)
  // Exposed for future use; currently the header X only detaches from this product
  const _handleDeleteAttribute = (attributeId: string) => {
    handleRemoveAttribute(attributeId);
    deleteAttribute.mutate(attributeId);
  };
  void _handleDeleteAttribute; // suppress unused warning

  // Add an attribute to the product configuration
  const handleAddAttribute = (attributeId: string) => {
    const attribute = orgAttributes.find(a => a.id === attributeId);
    if (!attribute) return;

    // Check if already added
    if (config.attributes.some(a => a.attributeId === attributeId)) return;

    // Can only have up to 3 attributes
    if (config.attributes.length >= 3) return;

    onChange({
      ...config,
      attributes: [
        ...config.attributes,
        {
          attributeId,
          attributeName: attribute.name,
          values: [],
        },
      ],
    });

    setOpenSections(prev => ({ ...prev, [attributeId]: true }));
  };

  // Remove an attribute from the product configuration
  const handleRemoveAttribute = (attributeId: string) => {
    onChange({
      ...config,
      attributes: config.attributes.filter(a => a.attributeId !== attributeId),
    });
  };

  // Toggle a value for an attribute
  const handleToggleValue = (attributeId: string, value: string) => {
    onChange({
      ...config,
      attributes: config.attributes.map(attr => {
        if (attr.attributeId !== attributeId) return attr;

        const hasValue = attr.values.includes(value);
        return {
          ...attr,
          values: hasValue
            ? attr.values.filter(v => v !== value)
            : [...attr.values, value],
        };
      }),
    });
  };

  // Handle creating a new attribute
  const handleCreateAttribute = () => {
    if (!newAttributeName.trim()) return;
    createAttribute.mutate(newAttributeName);
  };

  // Handle adding a new value to an attribute
  const handleAddValue = (attributeId: string) => {
    const value = newValueInputs[attributeId]?.trim();
    if (!value) return;

    createAttributeValue.mutate(
      { attributeId, value },
      {
        onSettled: () => {
          // Re-focus the input after creation so user can keep adding values
          requestAnimationFrame(() => {
            valueInputRefs.current[attributeId]?.focus();
          });
        },
      },
    );
    setNewValueInputs(prev => ({ ...prev, [attributeId]: '' }));
  };

  // Available attributes (not yet added to this product)
  const availableAttributes = orgAttributes.filter(
    a => !config.attributes.some(ca => ca.attributeId === a.id)
  );

  // Calculate variant count
  const variantCount = config.attributes.length > 0
    ? config.attributes.reduce((total, attr) => {
        return total * Math.max(attr.values.length, 1);
      }, 1)
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-sm">Variants</h4>
          <p className="text-xs text-muted-foreground">
            {compositeVariantMode
              ? 'Add an attribute (e.g. "Size") then select or create the values (e.g. Small, Medium, Large)'
              : 'Choose up to three variable attributes for this product'}
          </p>
        </div>
        {variantCount > 0 && (
          <Badge variant="secondary">
            {variantCount} variant{variantCount !== 1 ? 's' : ''} will be created
          </Badge>
        )}
      </div>

      {/* Contextual example for composite variant mode */}
      {compositeVariantMode && config.attributes.length === 0 && (
        <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800 p-3">
          <p className="text-xs text-amber-800 dark:text-amber-300 font-medium mb-1">How this works:</p>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Create an attribute like <strong>"Size"</strong>, then add values like <strong>"Small"</strong>, <strong>"Medium"</strong>, <strong>"Large"</strong>.
            Each value becomes a variant of this product with its own recipe quantities and price.
            The attribute name is the category (Size, Portion, etc.) and the values are the options customers choose from.
          </p>
        </div>
      )}

      {/* Added Attributes */}
      <div className="space-y-3">
        {config.attributes.map((attr) => {
          const orgAttr = orgAttributes.find(a => a.id === attr.attributeId);
          const isOpen = openSections[attr.attributeId] ?? true;

          return (
            <Collapsible
              key={attr.attributeId}
              open={isOpen}
              onOpenChange={(open) => setOpenSections(prev => ({ ...prev, [attr.attributeId]: open }))}
            >
              <div className="rounded-lg border bg-card">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform",
                        isOpen ? "" : "-rotate-90"
                      )} />
                      <span className="font-medium text-sm">{attr.attributeName}</span>
                      {attr.values.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {attr.values.length} selected
                        </Badge>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveAttribute(attr.attributeId);
                      }}
                      disabled={disabled}
                      title="Remove attribute from this product"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="p-3 pt-0 space-y-3">
                    {/* Value selection */}
                    <div className="flex flex-wrap gap-2">
                      {orgAttr?.values.map((value) => {
                        const isSelected = attr.values.includes(value);
                        return (
                          <span key={value} className="group/val relative inline-flex items-center">
                            <button
                              type="button"
                              onClick={() => handleToggleValue(attr.attributeId, value)}
                              disabled={disabled}
                              className={cn(
                                "px-3 py-1.5 text-sm rounded-full border transition-colors pr-7",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border hover:border-primary/50 hover:bg-muted"
                              )}
                            >
                              {value}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteValue(attr.attributeId, value);
                              }}
                              disabled={disabled || deleteAttributeValue.isPending}
                              className={cn(
                                "absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center transition-opacity",
                                "opacity-0 group-hover/val:opacity-100 focus:opacity-100",
                                isSelected
                                  ? "hover:bg-primary-foreground/20 text-primary-foreground"
                                  : "hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                              )}
                              title={`Delete "${value}"`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>

                    {/* Add new value */}
                    <div className="flex items-center gap-2">
                      <Input
                        ref={setValueInputRef(attr.attributeId)}
                        placeholder={compositeVariantMode ? "e.g. Small, Medium, Large..." : "Add new value..."}
                        value={newValueInputs[attr.attributeId] || ''}
                        onChange={(e) => setNewValueInputs(prev => ({
                          ...prev,
                          [attr.attributeId]: e.target.value,
                        }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddValue(attr.attributeId);
                          }
                        }}
                        className="h-8 text-sm"
                        disabled={disabled || createAttributeValue.isPending}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => handleAddValue(attr.attributeId)}
                        disabled={disabled || !newValueInputs[attr.attributeId]?.trim() || createAttributeValue.isPending}
                      >
                        {createAttributeValue.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>

      {/* Add Attribute */}
      {config.attributes.length < 3 && (
        <div className="flex items-center gap-2">
          {availableAttributes.length > 0 ? (
            <Select
              onValueChange={handleAddAttribute}
              disabled={disabled || attributesLoading}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select attribute..." />
              </SelectTrigger>
              <SelectContent>
                {availableAttributes.map((attr) => (
                  <SelectItem key={attr.id} value={attr.id}>
                    {attr.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}

          {!creatingAttribute ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCreatingAttribute(true)}
              disabled={disabled}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add another attribute
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                placeholder="Attribute name (e.g. Size)"
                value={newAttributeName}
                onChange={(e) => setNewAttributeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateAttribute();
                  }
                  if (e.key === 'Escape') {
                    setCreatingAttribute(false);
                    setNewAttributeName('');
                  }
                }}
                className="w-48 h-8 text-sm"
                disabled={createAttribute.isPending}
                autoFocus
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                className="h-8"
                onClick={handleCreateAttribute}
                disabled={!newAttributeName.trim() || createAttribute.isPending}
              >
                {createAttribute.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Add'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => {
                  setCreatingAttribute(false);
                  setNewAttributeName('');
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Info note */}
      {!compositeVariantMode && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <span className="inline-block w-1 h-1 rounded-full bg-amber-500" />
          Attributes and values can't be edited on variant products imported from the B2B catalog.
        </p>
      )}
    </div>
  );
}

